import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  BookOpenIcon,
  CalendarIcon,
  ChatIcon,
  CogIcon,
  HomeIcon,
  MailIcon,
  MenuIcon,
  DocumentIcon,
} from '@heroicons/react/outline';
import * as DropdownMenu from '@radix-ui/react-dropdown-menu';

import { useAuth } from '../../../contexts/AuthContext';

import {
  DATA_USE_URL,
  PRIVACY_POLICY_URL,
  TERMS_OF_SERVICE_URL,
} from '../../lib/constants';

import Logout from '../../svgs/Logout';
import Spinner from '../../svgs/Spinner';

import Avatar from '../Avatar';
import { UserType } from '../../../generated/graphql';
import classNames from 'classnames';
import { SvgIconComponent } from '../../types/svgs';
import hcLogo from '../../../assets/images/logo/logo-green-xl.png';
import { usePatientAuth } from '../../../contexts/PatientAuthContext';
import ProviderContexts from './ProviderContexts';

type SettingsMenuProps = {
  name?: string;
  email?: string;
  imageUrl?: string;
  userType?: UserType;
  isMobileDevice?: boolean;
  showPatientSessions?: boolean;
  externalMode?: boolean; // When rendering an iframe, this reduces the visual footprint of the settings menu
};

interface SettingsMenuItem {
  label: string;
  IconComponent: SvgIconComponent;
  path?: string;
  onSelect?: (e?: any) => void;
  className?: string;
  iconClassName?: string;
}

const SettingsMenu: React.FC<SettingsMenuProps> = ({
  name,
  email,
  imageUrl,
  userType = UserType.ProviderUser,
  isMobileDevice = false,
  showPatientSessions = false,
  externalMode = false,
}) => {
  const navigate = useNavigate();
  const { authedProviderUser, logout: providerLogout } = useAuth();
  const { logout: patientLogout } = usePatientAuth();

  const [isLogoutLoading, setIsLogoutLoading] = useState(false);

  const handleNavigate = (path: string) => {
    navigate(path);
  };

  const handleLogoutItemClick = async (event: Event) => {
    event.preventDefault();
    setIsLogoutLoading(true);

    try {
      if (userType === UserType.ProviderUser) {
        await providerLogout();
      } else if (userType === UserType.Patient) {
        await patientLogout();
      }
    } catch (error) {
      console.error('Logout error:', error);
      setIsLogoutLoading(false);
    }
  };

  const providerListItems: SettingsMenuItem[] = authedProviderUser
    ? [
        { label: 'Settings', IconComponent: CogIcon, path: 'settings' },
        {
          label: 'Log out',
          IconComponent: isLogoutLoading ? Spinner : Logout,
          onSelect: (e) => handleLogoutItemClick(e),
          className: isLogoutLoading ? 'text-red-100' : 'text-neutral-125',
          iconClassName: !isLogoutLoading && 'text-red-125',
        },
      ]
    : [];

  const patientMobileNavItems: SettingsMenuItem[] = [
    { label: 'Home', IconComponent: HomeIcon, path: '/client/home' },
    { label: 'Mail', IconComponent: MailIcon, path: '/client/follow-ups' },
    { label: 'Chat', IconComponent: ChatIcon, path: '/client/messages' },
    ...(showPatientSessions
      ? [
          {
            label: 'Services',
            IconComponent: CalendarIcon,
            path: '/client/sessions',
          },
        ]
      : []),
    { label: 'Library', IconComponent: BookOpenIcon, path: '/client/library' },
    { label: 'Files', IconComponent: DocumentIcon, path: '/client/files' },
  ];

  const patientDesktopMenuItems: SettingsMenuItem[] = [
    { label: 'Account', IconComponent: CogIcon, path: '/client/account' },
  ];

  const DropdownMenuItem: React.FC<{
    label: string;
    IconComponent: React.ElementType;
    path?: string;
    onSelect?: (e?: any) => void;
    className?: string;
    iconClassName?: string;
  }> = ({ path, label, IconComponent, onSelect, className, iconClassName }) => (
    <DropdownMenu.Item
      onClick={onSelect ? onSelect : () => handleNavigate(path)}
      className={classNames(
        'flex w-full cursor-pointer flex-row items-center justify-between px-4 py-2 text-left text-caption focus:bg-neutral-50 focus:outline-none',
        'text-neutral-125',
        className,
      )}
    >
      <span>{label}</span>
      <IconComponent
        className={classNames(
          'h-4 min-h-[16px] w-4 min-w-[16px]',
          iconClassName,
        )}
      />
    </DropdownMenu.Item>
  );

  return (
    <DropdownMenu.Root>
      <DropdownMenu.Trigger asChild>
        <div className="flex flex-row items-center rounded-lg px-2 py-1 hover:cursor-pointer hover:bg-neutral-75">
          <MenuIcon className="h-6 min-h-[24px] w-6 min-w-[24px] text-neutral-125" />
        </div>
      </DropdownMenu.Trigger>
      <DropdownMenu.Portal>
        <DropdownMenu.Content
          align="start"
          className={classNames(
            'absolute z-50 mt-2 w-72 rounded-lg bg-white py-2 shadow-100 ring-1 ring-black ring-opacity-5 focus:outline-none',
            externalMode ? '-right-10' : '-right-8',
          )}
        >
          <DropdownMenu.DropdownMenuGroup>
            <div className="flex flex-row items-center justify-between border-b border-neutral-75 px-4 pb-3 pt-1">
              <Avatar
                size="medium"
                className="mr-0.5"
                imageUrl={imageUrl}
                name={name}
              />
              <div className="flex flex-grow flex-col items-start justify-start overflow-hidden pl-2">
                <div className="truncate text-caption text-neutral-150">
                  {name}
                </div>
                <div className="w-full truncate text-small-caption text-secondary-125">
                  {email}
                </div>
              </div>
              {externalMode && (
                <img
                  src={hcLogo}
                  className="mb-2 ml-1 h-[1.125rem] w-[1.125rem] min-w-[1.125rem] flex-shrink-0"
                  alt="logo"
                />
              )}
            </div>
          </DropdownMenu.DropdownMenuGroup>
          {userType === UserType.ProviderUser && (
            <>
              <DropdownMenu.DropdownMenuGroup>
                {providerListItems.map((providerListItem, index) => (
                  <DropdownMenuItem
                    key={`providerListItem_${index}`}
                    label={providerListItem.label}
                    IconComponent={providerListItem.IconComponent}
                    path={providerListItem.path}
                    onSelect={providerListItem.onSelect}
                    className={providerListItem.className}
                    iconClassName={providerListItem.iconClassName}
                  />
                ))}
              </DropdownMenu.DropdownMenuGroup>
              <ProviderContexts />
            </>
          )}
          {userType === UserType.Patient && (
            <DropdownMenu.DropdownMenuGroup>
              {isMobileDevice ? (
                patientMobileNavItems.map((patientListItem, index) => (
                  <DropdownMenuItem
                    key={`patientListItem_${index}`}
                    label={patientListItem.label}
                    IconComponent={patientListItem.IconComponent}
                    iconClassName={
                      patientListItem.iconClassName || 'text-neutral-125'
                    }
                    path={patientListItem.path}
                    onSelect={patientListItem.onSelect}
                    className={patientListItem.className}
                  />
                ))
              ) : (
                <>
                  {patientDesktopMenuItems.map((menuItem, index) => (
                    <DropdownMenuItem
                      key={`patientDesktopMenuItem_${index}`}
                      label={menuItem.label}
                      IconComponent={menuItem.IconComponent}
                      iconClassName={
                        menuItem.iconClassName || 'text-neutral-125'
                      }
                      path={menuItem.path}
                      onSelect={menuItem.onSelect}
                      className={menuItem.className}
                    />
                  ))}
                  <DropdownMenuItem
                    key="patientLogout"
                    label="Log out"
                    IconComponent={isLogoutLoading ? Spinner : Logout}
                    onSelect={(e) => handleLogoutItemClick(e)}
                    className={
                      isLogoutLoading ? 'text-red-100' : 'text-neutral-125'
                    }
                    iconClassName={
                      !isLogoutLoading ? 'text-red-125' : undefined
                    }
                  />
                </>
              )}
            </DropdownMenu.DropdownMenuGroup>
          )}
          <DropdownMenu.DropdownMenuGroup>
            <div className="border-t-0.5 flex flex-row items-start justify-start border-neutral-75 px-4 pt-3 pb-1">
              <button
                className="mr-2 text-small-caption text-secondary-125 hover:underline"
                onClick={() => {
                  window.open(PRIVACY_POLICY_URL, '_blank');
                }}
              >
                Privacy
              </button>
              <button
                className="mr-2 text-small-caption text-secondary-125 hover:underline"
                onClick={() => {
                  window.open(TERMS_OF_SERVICE_URL, '_blank');
                }}
              >
                Terms
              </button>
              <button
                className="mr-2 text-small-caption text-secondary-125 hover:underline"
                onClick={() => {
                  window.open(DATA_USE_URL, '_blank');
                }}
              >
                Data use
              </button>
            </div>
          </DropdownMenu.DropdownMenuGroup>
        </DropdownMenu.Content>
      </DropdownMenu.Portal>
    </DropdownMenu.Root>
  );
};

export default SettingsMenu;
